import { BN, Program, Wallet } from '@coral-xyz/anchor'
import { NATIVE_MINT as WRAPPED_SOL_MINT } from '@solana/spl-token'
import { createAssociatedTokenAccountInstruction } from '@solana/spl-token'
import {
  Connection,
  Keypair,
  PublicKey,
  Transaction,
  TransactionInstruction,
  TransactionMessage,
  VersionedTransaction,
} from '@solana/web3.js'
import Decimal from 'decimal.js'
import {
  claimLiquidationPoolPositionInstruction,
  closeLiquidationPoolPositionInstruction,
  createUserReferralAccountInstruction,
  depositLiquidationPoolInstruction,
  findAssociatedTokenAddress,
  getHedgeMintPublicKey,
  getLiquidationPoolStatePublicKey,
  getLiquidationPoolUshAccountPublicKey,
  getReferralAccountPublicKey,
  getUserReferralAccountPublicKey,
  getUshMintPublicKey,
  getVaultSystemStatePublicKey,
  Vault,
} from 'hedge-web3'
import TransactionStep from '../../lib/TransactionStep'
import VaultType from '../accounts/VaultType'
import {
  getPoolUshAccountPublicKey,
  getReferralStatePublicKey,
  HEDGE_PROGRAM_PUBLICKEY,
} from '../utils/HedgeConstants'
import { GetReferralAccountPublicKeyForUser } from '../utils/Referral'
import {
  GenerateUserATAsInstructions,
  GenerateLiquidationPoolATAsInstructions,
} from './AssociatedTokenAccounts'
import { createCloseAccountInstruction } from '@solana/spl-token'

export async function getDepositLiquidationPoolTransactionStep(
  program: Program<Vault>,
  payerPublicKey: PublicKey,
  depositAmountInLamports: Decimal
): Promise<TransactionStep> {
  return new TransactionStep('Deposit Stability Pool', async () => {
    const { blockhash } = await program.provider.connection.getRecentBlockhash()
    const transaction = new Transaction({
      feePayer: payerPublicKey,
      recentBlockhash: blockhash,
    })
    const poolPosition = Keypair.generate()
    const ushMintPublicKey = await getUshMintPublicKey(HEDGE_PROGRAM_PUBLICKEY)
    const poolStateKey = await getLiquidationPoolStatePublicKey(HEDGE_PROGRAM_PUBLICKEY)
    const poolUSHAccount = await getLiquidationPoolUshAccountPublicKey(HEDGE_PROGRAM_PUBLICKEY)
    const userUshAccountPublicKey = await findAssociatedTokenAddress(
      HEDGE_PROGRAM_PUBLICKEY,
      payerPublicKey,
      ushMintPublicKey
    )
    if (!userUshAccountPublicKey) {
      // TODO Coudron: Return error or something?
      return
    }
    const poolStateInfo = await program.account.liquidationPoolState.fetch(poolStateKey)
    console.log('poolStateInfo', poolStateInfo)

    const userReferralAccountPublicKey = await getUserReferralAccountPublicKey(
      HEDGE_PROGRAM_PUBLICKEY,
      payerPublicKey
    )

    const vaultSystemStatePublicKey = await getVaultSystemStatePublicKey(HEDGE_PROGRAM_PUBLICKEY)
    let referralAccountPublicKey = await GetReferralAccountPublicKeyForUser(payerPublicKey, program)

    // Create User Referral Account if needed
    if (!(await program.provider.connection.getAccountInfo(userReferralAccountPublicKey))) {
      // Find referrer account
      // TODO, lookup if we need to attribute the referral to someone
      // if (typeof referrer === 'undefined') {
      // const vaultSystemStatePublicKey = await getVaultSystemStatePublicKey()
      //  referralAccountPublicKey = await getReferralAccountPublicKey(vaultSystemStatePublicKey)
      // } else {
      //   referralAccountPublicKey = await getReferralAccountPublicKey(referrer)
      // }

      console.log('No user referral account found. Adding instruction to create it.')
      transaction.add(
        await createUserReferralAccountInstruction(
          program,
          payerPublicKey,
          referralAccountPublicKey,
          userReferralAccountPublicKey
        )
      )
    }

    transaction.add(
      await depositLiquidationPoolInstruction(
        program,
        payerPublicKey,
        vaultSystemStatePublicKey,
        userUshAccountPublicKey,
        poolPosition.publicKey,
        new BN(depositAmountInLamports.toString()),
        userReferralAccountPublicKey,
        referralAccountPublicKey
      )
    )
    transaction.partialSign(poolPosition)
    return transaction
  })
}

export async function getCloseLiquidationPoolPositionTransactionStep(
  program: Program<Vault>,
  payerPublicKey: PublicKey,
  poolPositionPublicKey: PublicKey,
  positionEraPublicKey: PublicKey
): Promise<TransactionStep> {
  return new TransactionStep('Close Stability Pool Position', async () => {
    const { blockhash } = await program.provider.connection.getRecentBlockhash()
    const transaction = new Transaction({
      feePayer: payerPublicKey,
      recentBlockhash: blockhash,
    })
    const ushMintPublicKey = await getUshMintPublicKey(HEDGE_PROGRAM_PUBLICKEY)
    const hedgeMintPublickey = await getHedgeMintPublicKey(HEDGE_PROGRAM_PUBLICKEY)
    const vaultSystemStatePublicKey = await getVaultSystemStatePublicKey(HEDGE_PROGRAM_PUBLICKEY)
    const communityHedgeTokenAccount = await findAssociatedTokenAddress(
      HEDGE_PROGRAM_PUBLICKEY,
      vaultSystemStatePublicKey,
      hedgeMintPublickey
    )
    const userUshAccountPublicKey = await findAssociatedTokenAddress(
      HEDGE_PROGRAM_PUBLICKEY,
      payerPublicKey,
      ushMintPublicKey
    )
    const userHedgeAccountPublicKey = await findAssociatedTokenAddress(
      HEDGE_PROGRAM_PUBLICKEY,
      payerPublicKey,
      hedgeMintPublickey
    )
    const userHedgeAccountPublicKeyAccountInfo = await program.provider.connection.getAccountInfo(
      userHedgeAccountPublicKey
    )

    // Create a HEDGE associated token account if needed
    if (!userHedgeAccountPublicKeyAccountInfo) {
      console.log('Creating payer associated hedge account instruction')
      transaction.add(
        createAssociatedTokenAccountInstruction(
          payerPublicKey,
          userHedgeAccountPublicKey,
          payerPublicKey,
          hedgeMintPublickey
        )
      )
    }

    const userReferralAccountPublicKey = await getUserReferralAccountPublicKey(
      HEDGE_PROGRAM_PUBLICKEY,
      payerPublicKey
    )
    let referralAccountPublicKey = await GetReferralAccountPublicKeyForUser(payerPublicKey, program)
    const referralStatePublicKey = await getReferralStatePublicKey()

    // Create User Referral Account if needed
    if (!(await program.provider.connection.getAccountInfo(userReferralAccountPublicKey))) {
      console.log('No user referral account found. Adding instruction to create it.')
      transaction.add(
        await createUserReferralAccountInstruction(
          program,
          payerPublicKey,
          referralAccountPublicKey,
          userReferralAccountPublicKey
        )
      )
    }

    // Close the position
    transaction.add(
      await closeLiquidationPoolPositionInstruction(
        program,
        positionEraPublicKey,
        poolPositionPublicKey,
        payerPublicKey,
        userUshAccountPublicKey,
        userHedgeAccountPublicKey,
        communityHedgeTokenAccount,
        userReferralAccountPublicKey,
        referralAccountPublicKey,
        referralStatePublicKey
      )
    )

    return transaction
  })
}

export async function getClaimLiquidationPoolPositionsV0(
  program: Program<Vault>,
  payerPublicKey: PublicKey,
  poolPositionPublicKey: PublicKey,
  positionEraPublicKey: PublicKey
): Promise<TransactionStep[]> {
  // Get all vault types first
  const vaultTypesAll = (await program.account.vaultType.all()).map(
    (vt) => new VaultType(vt.account, vt.publicKey)
  )

  // Generate ATA instructions once (these will be included in first transaction)
  const userATAInstructions = await GenerateUserATAsInstructions(
    program,
    vaultTypesAll,
    payerPublicKey
  )
  const poolATAInstructions = await GenerateLiquidationPoolATAsInstructions(
    program,
    vaultTypesAll,
    payerPublicKey
  )

  // Split vault types into chunks of 4
  const vaultTypeChunks = chunk(vaultTypesAll, 4)
  
  // Create a transaction step for each chunk
  return vaultTypeChunks.map((vaultTypeChunk, index) => {
    return new TransactionStep(`Claim Stability Pool Position - ${index + 1}`, async () => {
      const { blockhash } = await program.provider.connection.getLatestBlockhash()

      // Build instructions array - only include ATA setup in first transaction
      const instructions = index === 0 
        ? [...userATAInstructions, ...poolATAInstructions]
        : []

      // Process each vault type in this chunk
      let closeWrappedSolAfter = false
      for (const vaultType of vaultTypeChunk) {
        const userCollateralATA = await findAssociatedTokenAddress(
          HEDGE_PROGRAM_PUBLICKEY,
          payerPublicKey,
          vaultType.collateralMint
        )

        instructions.push(
          await claimLiquidationPoolPositionInstruction(
            program,
            vaultType.publicKey,
            vaultType.collateralMint,
            poolPositionPublicKey,
            payerPublicKey,
            userCollateralATA
          )
        )

        if (vaultType.collateralMint.equals(WRAPPED_SOL_MINT)) {
          closeWrappedSolAfter = true
        }
      }

      // Add wrapped SOL closing instruction if needed for this chunk
      if (closeWrappedSolAfter) {
        const wrappedSolATA = await findAssociatedTokenAddress(
          HEDGE_PROGRAM_PUBLICKEY,
          payerPublicKey,
          WRAPPED_SOL_MINT
        )
        instructions.push(
          createCloseAccountInstruction(
            wrappedSolATA,
            payerPublicKey,
            payerPublicKey
          )
        )
      }

      // Build and return versioned transaction
      const messageV0 = new TransactionMessage({
        payerKey: payerPublicKey,
        recentBlockhash: blockhash,
        instructions,
      }).compileToV0Message()

      return new VersionedTransaction(messageV0)
    })
  })
}

// Helper function to split array into chunks
function chunk<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size))
  }
  return chunks
}

export async function getClaimLiquidationPoolPositions(
  program: Program<Vault>,
  payerPublicKey: PublicKey,
  poolPositionPublicKey: PublicKey,
  positionEraPublicKey: PublicKey
): Promise<TransactionStep> {
  return new TransactionStep('Claim Stability Pool Position', async () => {
    const { blockhash } = await program.provider.connection.getRecentBlockhash()
    const transaction = new Transaction({
      feePayer: payerPublicKey,
      recentBlockhash: blockhash,
    })
    const ushMintPublicKey = await getUshMintPublicKey(HEDGE_PROGRAM_PUBLICKEY)
    const hedgeMintPublickey = await getHedgeMintPublicKey(HEDGE_PROGRAM_PUBLICKEY)
    const vaultSystemStatePublicKey = await getVaultSystemStatePublicKey(HEDGE_PROGRAM_PUBLICKEY)
    const communityHedgeTokenAccount = await findAssociatedTokenAddress(
      HEDGE_PROGRAM_PUBLICKEY,
      vaultSystemStatePublicKey,
      hedgeMintPublickey
    )
    const userUshAccountPublicKey = await findAssociatedTokenAddress(
      HEDGE_PROGRAM_PUBLICKEY,
      payerPublicKey,
      ushMintPublicKey
    )
    const userHedgeAccountPublicKey = await findAssociatedTokenAddress(
      HEDGE_PROGRAM_PUBLICKEY,
      payerPublicKey,
      hedgeMintPublickey
    )
    const userHedgeAccountPublicKeyAccountInfo = await program.provider.connection.getAccountInfo(
      userHedgeAccountPublicKey
    )

    // Get all the vault types so we can iterate them to claim
    const vaultTypes = (await program.account.vaultType.all()).map((vaultType) => {
      return new VaultType(vaultType.account, vaultType.publicKey)
    })

    // Create any needed user associated token accounts
    const userAtcInstructions = await GenerateUserATAsInstructions(
      program,
      vaultTypes,
      payerPublicKey
    )
    if (userAtcInstructions.length > 0) {
      transaction.add(...userAtcInstructions)
    }

    // Create any needed pool associated token accounts
    const liquidationPoolAtcInstructions = await GenerateLiquidationPoolATAsInstructions(
      program,
      vaultTypes,
      payerPublicKey
    )
    if (liquidationPoolAtcInstructions.length > 0) {
      transaction.add(...liquidationPoolAtcInstructions)
    }

    // Iterate the vault types and claim each
    let closeWrappedSolAfter = false
    for (const vaultType of vaultTypes) {
      const payerAssociatedTokenAccount = await findAssociatedTokenAddress(
        HEDGE_PROGRAM_PUBLICKEY,
        payerPublicKey,
        vaultType.collateralMint
      )
      console.log(
        `Adding claim instruction for ${vaultType.name} | Collateral Mint: ${vaultType.collateralMint}`
      )

      transaction.add(
        await claimLiquidationPoolPositionInstruction(
          program,
          vaultType.publicKey,
          vaultType.collateralMint,
          poolPositionPublicKey,
          payerPublicKey,
          payerAssociatedTokenAccount
        )
      )
      if (vaultType.collateralMint.toString() === WRAPPED_SOL_MINT.toString()) {
        closeWrappedSolAfter = true
      }
    }

    if (closeWrappedSolAfter) {
      console.log(`Adding unwrap SOL instruction`)
      const payerWrappedSolAssociatedTokenAccount = await findAssociatedTokenAddress(
        HEDGE_PROGRAM_PUBLICKEY,
        payerPublicKey,
        WRAPPED_SOL_MINT
      )
      transaction.add(
        createCloseAccountInstruction(
          payerWrappedSolAssociatedTokenAccount,
          payerPublicKey,
          payerPublicKey,
        )
      )
    }

    return transaction
  })
}

export async function getCleanupLiquidationPoolPositionsV0(
  program: Program<Vault>,
  payerPublicKey: PublicKey,
  poolPositionPublicKey: PublicKey
): Promise<TransactionStep> {
  return new TransactionStep('Cleanup (v0)', async () => {
    // Build the close/cleanup instruction
    const cleanupIx = await program.methods
      .closeClaimedLiquidationPoolPosition()
      .accounts({
        poolPosition: poolPositionPublicKey,
        payer: payerPublicKey
      })
      .instruction();

    const instructions = [cleanupIx];

    // Build a versioned transaction:
    const { blockhash } = await program.provider.connection.getLatestBlockhash();
    const messageV0 = new TransactionMessage({
      payerKey: payerPublicKey,
      recentBlockhash: blockhash,
      instructions
    }).compileToV0Message();

    const versionedTx = new VersionedTransaction(messageV0);
    return versionedTx;
  });
}


export async function getCleanupLiquidationPoolPositions(
  program: Program<Vault>,
  payerPublicKey: PublicKey,
  poolPositionPublicKey: PublicKey
): Promise<TransactionStep> {
  return new TransactionStep('Cleanup Stability Pool Position', async () => {
    const { blockhash } = await program.provider.connection.getRecentBlockhash()
    const transaction = new Transaction({
      feePayer: payerPublicKey,
      recentBlockhash: blockhash,
    })
    const ushMintPublicKey = await getUshMintPublicKey(HEDGE_PROGRAM_PUBLICKEY)
    const hedgeMintPublickey = await getHedgeMintPublicKey(HEDGE_PROGRAM_PUBLICKEY)
    const vaultSystemStatePublicKey = await getVaultSystemStatePublicKey(HEDGE_PROGRAM_PUBLICKEY)
    const communityHedgeTokenAccount = await findAssociatedTokenAddress(
      HEDGE_PROGRAM_PUBLICKEY,
      vaultSystemStatePublicKey,
      hedgeMintPublickey
    )
    const userUshAccountPublicKey = await findAssociatedTokenAddress(
      HEDGE_PROGRAM_PUBLICKEY,
      payerPublicKey,
      ushMintPublicKey
    )
    const userHedgeAccountPublicKey = await findAssociatedTokenAddress(
      HEDGE_PROGRAM_PUBLICKEY,
      payerPublicKey,
      hedgeMintPublickey
    )
    const userHedgeAccountPublicKeyAccountInfo = await program.provider.connection.getAccountInfo(
      userHedgeAccountPublicKey
    )

    transaction.add(
      await program.methods
        .closeClaimedLiquidationPoolPosition()
        .accounts({
          poolPosition: poolPositionPublicKey,
          payer: payerPublicKey,
        })
        .instruction()
    )

    return transaction
  })
}
